import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../../config/axiosConfig';
import 'react-toastify/dist/ReactToastify.css';
import { API_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import { Modal, ModalTitle, ModalContent, ModalActions } from '../basic';

interface AddOpponentModalProps {
  onClose: () => void;
  onOpponentAdded?: () => void;
  editOpponent?: any | null;
}

// Zod validation schema
const opponentSchema = z.object({
  name: z.string().min(1, 'Opponent name is required').trim(),
  clientId: z.string().min(1, 'Client is required'),
});

type OpponentFormData = z.infer<typeof opponentSchema>;

const AddOpponentModal: React.FC<AddOpponentModalProps> = ({ onClose, onOpponentAdded, editOpponent }) => {
  const [isLoading, setIsLoading] = useState(false);
  const clientIdFromSession = sessionManager.getClientId();

  // Initialize react-hook-form with zod validation
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    reset,
  } = useForm<OpponentFormData>({
    resolver: zodResolver(opponentSchema),
    defaultValues: {
      name: '',
      clientId: '',
    },
  });

  // Fetch clients on component mount
  useEffect(() => {
    if (clientIdFromSession) {
      setValue('clientId', clientIdFromSession);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIdFromSession]);

  // Pre-fill form if editing
  useEffect(() => {
    if (editOpponent) {
      reset({
        name: editOpponent.name || '',
        clientId: editOpponent.clientId || '',
      });
    }
  }, [editOpponent, reset]);

  const handleClose = () => {
    if (!isLoading && !isSubmitting) {
      onClose();
    }
  };

  const onSubmit = async (data: OpponentFormData) => {
    const token = sessionManager.getToken();
    if (!token) {
      showErrorMsg('Authentication token not found. Please log in again.');
      return;
    }

    setIsLoading(true);
    try {
      let response;
      if (editOpponent && editOpponent._id) {
        // Update opponent
        const submitData = {
          ...data,
          _id: editOpponent._id,
        };
        response = await api.put(API_URLS.UPDATE_OPPONENT, submitData);
        if (response.data.success) {
          showSuccessMsg('Opponent updated successfully!');
          onOpponentAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to update opponent');
        }
      } else {
        // Add opponent
        response = await api.post(API_URLS.ADD_OPPONENT, data);
        if (response.data.success) {
          showSuccessMsg('Opponent added successfully!');
          onOpponentAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to add opponent');
        }
      }
    } catch (error: any) {
      console.error('Error adding opponent:', error);

      // Handle different types of errors
      if (error.response?.status === 401) {
        showErrorMsg('Session expired. Please log in again.');
      } else if (error.response?.data?.message) {
        showErrorMsg(error.response.data.message);
      } else if (error.message) {
        showErrorMsg(`Error: ${error.message}`);
      } else {
        showErrorMsg('Failed to add opponent. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} maxWidth="md" isLoading={isLoading || isSubmitting}>
      <ModalTitle>{editOpponent ? 'Edit Opponent' : 'Add Opponent'}</ModalTitle>

      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)} id="opponent-form">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-gray-700 font-medium mb-1">Opponent Name *</label>
              <input
                type="text"
                {...register('name')}
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading || isSubmitting}
                placeholder="Enter opponent name"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
            </div>
          </div>
        </form>
      </ModalContent>

      <ModalActions>
        <button
          type="button"
          onClick={handleClose}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors disabled:opacity-50"
          disabled={isLoading || isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="opponent-form"
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          disabled={isLoading || isSubmitting}
        >
          {isLoading || isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {editOpponent ? 'Saving...' : 'Adding...'}
            </>
          ) : editOpponent ? (
            'Save Changes'
          ) : (
            'Add Opponent'
          )}
        </button>
      </ModalActions>
    </Modal>
  );
};

export default AddOpponentModal;
